import React, { useState, useCallback } from 'react';
import { useAuthContext } from '../../../context/AuthContext';
import useLanguage from '../../../hooks/useLanguage';

const DesktopForgotPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [emailSent, setEmailSent] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);

  const { forgotPassword, error: authError, clearError } = useAuthContext();

  const validateEmail = useCallback((value) => {
    if (!value) return t('desktop.forgotPassword.errors.emailRequired');
    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value)) {
      return t('desktop.forgotPassword.errors.emailInvalid');
    }
    return '';
  }, [t]);

  const handleEmailChange = (event) => {
    const nextValue = event.target.value;
    setEmail(nextValue);

    if (errors.email) {
      setErrors((prev) => ({ ...prev, email: '' }));
    }
    if (authError) {
      clearError();
    }
  };

  const handleEmailBlur = () => {
    setTouchedEmail(true);
    setErrors((prev) => ({
      ...prev,
      email: validateEmail(email)
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading) return;

    const emailError = validateEmail(email);
    if (emailError) {
      setErrors({ email: emailError });
      setTouchedEmail(true);
      return;
    }

    setIsLoading(true);
    setErrors({});
    clearError();

    try {
      console.log('[DesktopForgotPasswordModal] Sending password reset request for:', email);
      const response = await forgotPassword(email);
      console.log('[DesktopForgotPasswordModal] Password reset successful:', response);

      setEmailSent(true);
      if (onSuccess) {
        onSuccess(email);
      }
    } catch (error) {
      console.error('[DesktopForgotPasswordModal] Password reset failed:', error);
      if (error.message && error.message.includes('rate limit')) {
        setErrors({
          general: t('desktop.forgotPassword.errors.rateLimited')
        });
      } else {
        setErrors({
          general: error.message || t('desktop.forgotPassword.errors.submitFailed')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setErrors({});
    setEmailSent(false);
    setTouchedEmail(false);
    setIsLoading(false);
    clearError();
    onClose();
  };

  const handleBackToReset = () => {
    setEmailSent(false);
    setErrors({});
    clearError();
  };

  if (!isOpen) return null;

  return (
    <div
      className="desktop-forgot-modal-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="desktop-forgot-password-title"
    >
      <div
        className="desktop-forgot-modal-card"
        onClick={(event) => event.stopPropagation()}
      >
        {!emailSent ? (
          <>
            <div className="desktop-forgot-modal-header">
              <div className="desktop-forgot-modal-title-wrapper">
                <div className="desktop-forgot-modal-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.9 1 3 1.9 3 3V19C3 20.1 3.9 21 5 21H11V19H5V3H13V7H17V9H19V11H21V9ZM13 13V17H15V19H13C12.4 19 12 18.6 12 18V13H10L13 10L16 13H13Z"
                      fill="currentColor"
                    />
                  </svg>
                </div>
                <div>
                  <h2 id="desktop-forgot-password-title">{t('desktop.forgotPassword.title')}</h2>
                  <p>{t('desktop.forgotPassword.description')}</p>
                </div>
              </div>
              <button
                className="desktop-modal-close"
                onClick={handleClose}
                aria-label={t('desktop.forgotPassword.actions.close')}
                type="button"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>

            {(errors.general || authError) && (
              <div className="desktop-general-message desktop-general-message-error">
                <span>{errors.general || authError}</span>
                <button
                  type="button"
                  className="desktop-clear-message"
                  onClick={() => {
                    setErrors((prev) => ({ ...prev, general: '' }));
                    clearError();
                  }}
                  title={t('desktop.forgotPassword.actions.dismissMessage')}
                >
                  ×
                </button>
              </div>
            )}

            <form onSubmit={handleSubmit} className="desktop-forgot-modal-form">
              <div className="desktop-form-field" style={{ '--field-index': 0 }}>
                <div
                  className={`desktop-input-container desktop-floating-label ${email ? 'has-value' : ''} ${
                    errors.email ? 'error' : ''
                  }`}
                >
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    className="desktop-form-input"
                    placeholder=" "
                    autoComplete="email"
                    required
                    disabled={isLoading}
                  />
                  <label className="desktop-floating-label-text">{t('desktop.forgotPassword.fields.email')}</label>
                  {email && !errors.email && (
                    <div className="desktop-input-icon success">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path
                          d="M13.5 4.5L6 12L2.5 8.5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                {errors.email && touchedEmail && (
                  <span className="desktop-error-message">{errors.email}</span>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className={`desktop-submit-button desktop-forgot-submit ${isLoading ? 'loading' : ''}`}
              >
                {isLoading ? <div className="desktop-loading-spinner" /> : t('desktop.forgotPassword.actions.submit')}
              </button>
            </form>
          </>
        ) : (
          <div className="desktop-forgot-success">
            <div className="desktop-forgot-success-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22C6.477 22 2 17.523 2 12C2 6.478 6.477 2 12 2C17.522 2 22 6.478 22 12C22 17.523 17.522 22 12 22ZM10.9999 16.0001L17.071 9.929L15.6568 8.51477L10.9999 13.1716L8.82824 11L7.41402 12.4142L10.9999 16.0001Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <h2>{t('desktop.forgotPassword.actions.successTitle')}</h2>
            <p>{t('desktop.forgotPassword.actions.successBody', { email })}</p>
            <div className="desktop-forgot-success-actions">
              <button type="button" className="desktop-submit-button ghost" onClick={handleBackToReset}>
                {t('desktop.forgotPassword.actions.sendAnother')}
              </button>
              <button type="button" className="desktop-submit-button" onClick={handleClose}>
                {t('desktop.forgotPassword.actions.backToLogin')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopForgotPasswordModal;
